'use client';

/**
 * Input — v4 labeled text input.
 * Accessible: label is bound to the input via htmlFor/id. Error text slot
 * sets aria-invalid and links via aria-describedby.
 */

import { useId, type ReactNode } from 'react';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className' | 'style'> {
  label?: string;
  error?: string;
  /** Leading icon element; adds left padding for it. */
  leftIcon?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
}

export function Input({
  label,
  error,
  leftIcon,
  id,
  className,
  style,
  wrapperStyle,
  ...rest
}: InputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = `${inputId}-error`;

  const classes = [
    'v4-input',
    leftIcon ? 'v4-input--icon' : '',
    error ? 'v4-input--error' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="v4-field" style={wrapperStyle}>
      {label && (
        <label className="v4-field__label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        {leftIcon && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              color: 'var(--v4-fg-4)',
              pointerEvents: 'none',
            }}
          >
            {leftIcon}
          </span>
        )}
        <input
          id={inputId}
          className={classes}
          style={style}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          {...rest}
        />
      </div>
      {error && (
        <p id={errorId} className="v4-field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default Input;
